/**
 * Dashboard — "Today". Memory Lane V2: the emotional home.
 *
 * Not a widget grid — a gentle walk through today:
 *   Welcome → Today's priority → What feels most important →
 *   Lane → Your Journey → The people helping you → Recent progress →
 *   A place for you.
 * Coastal-morning light: warm ivory, morning sky, deep ocean teal.
 * Whitespace instead of borders. Serif headings. Slow, breathing motion.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    MessageCircle, ArrowRight, PencilLine, History,
    HeartHandshake, ChevronDown, Map as MapIcon, Heart,
} from "lucide-react";
import { SECTION_COLORS } from "@/lib/sectionColors";
import { runActivationHandoff } from "@/lib/activationHandoff";
import { toast } from "sonner";
import LaneMemoryCard from "@/components/LaneMemoryCard";
import CaregiverOrgBadge from "@/components/CaregiverOrgBadge";
import DailyCheckIn from "@/components/DailyCheckIn";
import FirstVisitCheckIn from "@/components/FirstVisitCheckIn";
import CaregiverCheckIn from "@/components/CaregiverCheckIn";
import PlanReadyCard from "@/components/PlanReadyCard";
import { setFirstVisitMode } from "@/lib/firstVisit";
import DripWelcomeBanner from "@/components/DripWelcomeBanner";
import { CareDetailsDialog } from "@/components/CareDetailsDialog";
import { BriefingStack } from "@/components/home/BriefingStack";
import useDripReferral from "@/hooks/useDripReferral";
import { JOURNEY_AREAS, currentStageKey, dashboardJourneyAreas, dashboardShortTitle } from "@/lib/roadmapStages";

const CAREGIVER_QUOTES = [
    { text: "Some days the win is just that we both ate. That counts. That always counts.", attribution: "A daughter caring for her mom" },
    { text: "You will forget what day it is. You will not forget that you loved them.", attribution: "A son, three years in" },
    { text: "Asking for help isn't failing. It's the only way I'm still standing.", attribution: "A spouse caregiver" },
    { text: "You are not behind. There is no behind in this. You are exactly where you need to be today.", attribution: "A daughter-in-law caregiver" },
    { text: "Take the nap. Cancel the thing. The world will wait. Your body will not.", attribution: "A spouse, five years in" },
    { text: "On the worst nights I told myself: I only have to get through the next ten minutes. Then ten more.", attribution: "A daughter caring at home" },
    { text: "The person you knew is still in there — just behind a door you can't always open. Sit on their side of the door anyway.", attribution: "A daughter, late stage" },
    { text: "Nobody told me grief could start while they were still here. It's okay if yours has too.", attribution: "A son caring for his dad" },
];

function timeGreeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
}

function pickName({ story, profile }) {
    return (story?.caregiver?.name || profile?.caregiver_name || "").trim();
}

// A name auto-derived from the email local-part ("sarahmiller1962") must
// never appear in the greeting — "Good evening." alone is better.
function emailDerived(name, email) {
    const n = (name || "").trim().toLowerCase();
    if (!n || !email) return false;
    const local = String(email).split("@")[0].toLowerCase();
    return n === local || n === (local.split(/[._+-]/)[0] || local);
}

const norm = (s) => (s === "starting_soon" ? "referred" : s);

/* Section label — small caps with a color dot tied to the matching nav tab,
 * so each dashboard band reads as its own zone. */
function SectionEyebrow({ children, testid, color = "#4F7F95" }) {
    return (
        <p data-testid={testid} className="text-[11px] uppercase tracking-[0.22em] font-semibold inline-flex items-center gap-2" style={{ color }}>
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} aria-hidden="true" />
            {children}
        </p>
    );
}

/* One "room" of the home — a color-coded band matching its nav tab,
 * closed until opened. Open state persists per device. */
function CollapsibleSection({ id, testid, icon: Icon, colorKey, title, summary, defaultOpen = false, children }) {
    const c = SECTION_COLORS[colorKey] || SECTION_COLORS.home;
    const [open, setOpen] = useState(() => {
        try {
            const v = localStorage.getItem(`mlcn_dash_room_${id}`);
            return v === null ? defaultOpen : v === "1";
        } catch { return defaultOpen; }
    });
    const toggle = () => setOpen(o => {
        const n = !o;
        try { localStorage.setItem(`mlcn_dash_room_${id}`, n ? "1" : "0"); } catch { /* ignore quota */ }
        return n;
    });
    return (
        <section data-testid={testid} className="mt-3.5 rounded-2xl bg-white border border-[#E4E8EC] overflow-hidden shadow-[0_1px_2px_rgba(16,42,67,0.04)]"
            style={{ borderLeft: `3px solid ${c.accent}` }}>
            <button type="button" onClick={toggle} aria-expanded={open} data-testid={`${testid}-toggle`}
                className="w-full flex items-center gap-3.5 px-5 md:px-6 py-4 text-left min-h-[64px]"
                style={{ backgroundColor: c.soft }}>
                <Icon size={16} strokeWidth={1.5} className="shrink-0" style={{ color: c.accent }} aria-hidden="true" />
                <span className="flex-1 min-w-0">
                    <span className="block text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: c.accent }}>{title}</span>
                    {summary && (
                        <span className="block mt-0.5 text-sm text-[#2A3330]/65 leading-snug truncate">{summary}</span>
                    )}
                </span>
                <ChevronDown size={16} strokeWidth={1.8}
                    className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    style={{ color: c.accent }} aria-hidden="true" />
            </button>
            {open && <div className="px-5 md:px-7 py-6 bg-white border-t border-[#EDF0F3]">{children}</div>}
        </section>
    );
}

export default function Dashboard() {
    const sid = getSessionId();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const planJustReady = searchParams.get("plan") === "ready";
    const { user } = useAuth();
    const drip = useDripReferral();

    const [profile, setProfile] = useState({});
    const [intake, setIntake] = useState(null);
    const [story, setStory] = useState(null);
    const [wellness, setWellness] = useState(null);
    const [today, setToday] = useState({ behaviors: [], checkins: [], wellness: [] });
    const [appointments, setAppointments] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [careDetailsOpen, setCareDetailsOpen] = useState(false);
    // First-visit home (no intake, no activity yet): check-in first, chrome minimal.
    const [firstVisit, setFirstVisit] = useState(false);
    const [firstTapped, setFirstTapped] = useState(false);
    // Caregiver check-in ("How are you doing today?") — on a first visit the
    // loved-one card is revealed only after the caregiver has answered.
    const [caregiverDone, setCaregiverDone] = useState(false);

    const userRole = user && user !== false ? user.role : null;

    // Demo continuity: after the profile loads, persist which homepage demo
    // brought them here + pre-mark the Sample Journey stage (once).
    useEffect(() => {
        if (loading || !user || user === false) return;
        runActivationHandoff({
            sid,
            hasStage: Boolean(profile?.primary_journey_area),
            onStage: (key) => setProfile(p => ({ ...(p || {}), primary_journey_area: key })),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                const [pr, ir, ms, wn, lm] = await Promise.all([
                    api.get(`/profile/${sid}`).catch(() => ({ data: {} })),
                    api.get(`/intake/${sid}`).catch(() => ({ data: false })),
                    api.get(`/my-story/${sid}`).catch(() => ({ data: null })),
                    api.get(`/wellness/${sid}`).catch(() => ({ data: null })),
                    api.get(`/lane/memory/${sid}`).catch(() => ({ data: null })),
                ]);
                if (!active) return;
                const laneMem = lm.data || {};
                setProfile({
                    ...(pr.data || {}),
                    caregiver_name: pr.data?.caregiver_name || laneMem.caregiver_name || "",
                    loved_one_name: pr.data?.loved_one_name || laneMem.loved_one_name || "",
                });
                setIntake(ir.data || false);
                setStory(ms.data || null);
                setWellness(wn.data || null);

                // Serialized second wave — avoids the dev rate limiter.
                let behaviorsRes = { data: [] };
                let checkinsRes = { data: { checkins: [] } };
                let apptRes = { data: null };
                let provRes = { data: { providers: [] } };
                try { behaviorsRes = await api.get(`/behavior/${sid}`); } catch { /* zero-state */ }
                try { checkinsRes = await api.get(`/me/daily-checkin/history?days=90`); } catch { /* zero-state */ }
                try { apptRes = await api.get(`/appointment/${sid}`); } catch { /* zero-state */ }
                try { provRes = await api.get(`/care-team/providers/${sid}`); } catch { /* zero-state */ }
                if (!active) return;

                const behaviorsAll = Array.isArray(behaviorsRes.data) ? behaviorsRes.data : [];
                const checkinsAll = checkinsRes.data?.checkins || [];
                const cutoff = Date.now() - 24 * 60 * 60 * 1000;
                const inLastDay = (t) => { try { return t && new Date(t).getTime() >= cutoff; } catch { return false; } };
                setToday({
                    behaviors: behaviorsAll.filter(b => inLastDay(b.created_at)),
                    checkins: checkinsAll.filter(c => inLastDay(c.created_at)),
                    wellness: wn.data?.recent ? (wn.data.recent || []).filter(w => inLastDay(w.created_at)) : [],
                });
                setAppointments(apptRes.data ? [apptRes.data] : []);
                setProviders(provRes.data?.providers || []);

                // Brand-new caregiver: no intake, no story, no wellness, no
                // behavior logs, no check-ins — the first-visit home applies.
                const hasIntakeL = !!(ir.data && (ir.data.updated_at || ir.data.created_at || ir.data.predx || ir.data.estimated_stage));
                const hasStoryL = !!(ms.data && (ms.data.loved_one?.name || ms.data.caregiver?.name || ms.data.updated_at));
                const hasWellnessL = !!(wn.data && (wn.data.last_check_in || (wn.data.recent || []).length > 0));
                const isNew = userRole !== "admin" && !hasIntakeL && !hasStoryL && !hasWellnessL
                    && behaviorsAll.length === 0 && checkinsAll.length === 0;
                setFirstVisit(isNew);

                const shown = sessionStorage.getItem("mlcn_intake_welcome_shown") === "1";
                if (!ir.data && userRole !== "admin" && !shown && !isNew) {
                    sessionStorage.setItem("mlcn_intake_welcome_shown", "1");
                    toast("Welcome — we're so glad you're here.", {
                        description: "When you're ready, tell me a little about you and your loved one. It helps Lane walk beside you — nothing is required today.",
                        duration: 8000,
                        closeButton: true,
                        action: {
                            label: "Tell Lane our story",
                            onClick: () => navigate("/intake"),
                        },
                    });
                }
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => { active = false; };
    }, [sid, navigate, userRole]);

    const lovedName = (story?.loved_one?.name) || profile.loved_one_name || "";
    const cgName = pickName({ story, profile })
        || ((user && user !== false && user.name && !emailDerived(user.name, user.email))
            ? user.name.trim().split(" ")[0] : "");

    // Dismissed / snoozed attention keys. Server-persisted (cross-device);
    // localStorage is the instant initial value + offline fallback.
    const [dismissedUntil, setDismissedUntil] = useState(() => {
        try { return JSON.parse(localStorage.getItem("mlcn_attention_snoozed") || "{}"); } catch { return {}; }
    });
    const [quote] = useState(() => CAREGIVER_QUOTES[Math.floor(Date.now() / 86400000) % CAREGIVER_QUOTES.length]);

    const isSnoozed = (key) => {
        const until = dismissedUntil[key];
        if (!until) return false;
        return new Date(until).getTime() > Date.now();
    };

    useEffect(() => {
        if (!user || user === false) return;
        let active = true;
        api.get("/attention/snoozed").then(r => {
            if (!active) return;
            const server = r.data?.snoozed || {};
            setDismissedUntil(server);
            try { localStorage.setItem("mlcn_attention_snoozed", JSON.stringify(server)); } catch { /* ignore */ }
        }).catch(() => { /* offline or unauth — stay on the local copy */ });
        return () => { active = false; };
    }, [user]);

    const snooze = (key, days) => {
        const until = new Date(Date.now() + days * 86400000).toISOString();
        const next = { ...dismissedUntil, [key]: until };
        setDismissedUntil(next);
        try { localStorage.setItem("mlcn_attention_snoozed", JSON.stringify(next)); } catch { /* ignore quota */ }
        api.post("/attention/snooze", { key, days }).catch(() => { /* silent */ });
        toast(days >= 30 ? "Dismissed for a month." : `Snoozed for ${days} day${days === 1 ? "" : "s"}.`);
    };

    const burnoutBand = wellness?.bands?.burnout;
    const lastCheckIn = wellness?.last_check_in;
    const daysSinceCheckIn = lastCheckIn ? Math.floor((Date.now() - new Date(lastCheckIn).getTime()) / 86400000) : null;

    const primaryKey = profile?.primary_journey_area || (intake ? currentStageKey({
        predx: intake.predx, estimated_stage: intake.estimated_stage,
    }) : null);
    const primaryArea = JOURNEY_AREAS.find(a => a.key === primaryKey) || null;
    const alsoAreas = (profile?.also_dealing_with || [])
        .filter(k => k !== primaryKey)
        .map(k => JOURNEY_AREAS.find(a => a.key === k))
        .filter(Boolean);

    // ---- Today's priority — real saved data only.
    const attention = useMemo(() => {
        const items = [];
        if (burnoutBand === "high") {
            items.push({
                key: "burnout", major: true,
                title: "Your burnout has been running high.",
                detail: "One quiet thing for you today. The rest can wait.",
                to: "/wellness", cta: "Open a 2-minute check-in",
                when: "Needs attention",
            });
        }
        const fall = today.behaviors.find(b => /fall|safety/i.test(b.behavior_type || ""));
        if (fall) {
            items.push({
                key: "safety", major: true,
                title: "A safety concern was noted today.",
                detail: fall.behavior_type,
                to: "/behavior", cta: "Review the note",
                when: "Needs attention",
            });
        }
        // Appointment surfacing moved to the server-side briefing engine
        // (BriefingStack) — it only appears when a visit is within 7 days,
        // with the summary already prepared. No duplicate item here.
        providers.filter(p => norm(p.status) === "paused").slice(0, 2).forEach(p => {
            items.push({
                key: `paused-${p.id}`, major: true,
                title: `${p.name} is paused.`,
                detail: "Worth checking whether it can restart, or what fills the gap.",
                to: "/care-team", cta: "Update this service",
                when: "Needs attention",
            });
        });
        providers.filter(p => norm(p.status) === "referred").slice(0, 2).forEach(p => {
            items.push({
                key: `ref-${p.id}`, major: true,
                title: `${p.name} was referred but hasn't started.`,
                detail: "A quick call can get a start date on the calendar.",
                to: "/care-team", cta: "Mark it active when it starts",
                when: "Needs attention",
            });
        });
        if (daysSinceCheckIn !== null && daysSinceCheckIn > 7) {
            items.push({
                key: "checkin", major: true,
                title: `It's been ${daysSinceCheckIn} days since a wellness check-in.`,
                detail: "Even one tap tells Lane how you're doing.",
                to: "/wellness", cta: "Do a quick check-in",
                when: "Needs attention",
            });
        }
        return items.slice(0, 4).filter(i => !isSnoozed(i.key));
    }, [burnoutBand, today.behaviors, appointments, providers, daysSinceCheckIn, dismissedUntil]);

    // Recent progress — today's gentle log (non-urgent).
    const recent = useMemo(() => {
        const rows = [];
        today.behaviors.slice(0, 2).forEach((b, i) => rows.push({
            key: `b-${i}`, testid: `today-behavior-${i}`,
            title: `${(b.behavior_type || "Behavior").replace(/_/g, " ")} noted`,
            detail: b.trigger ? `Possible trigger: ${b.trigger}` : (b.notes ? `${(b.notes || "").slice(0, 90)}${(b.notes || "").length > 90 ? "…" : ""}` : ""),
            when: "Today", tone: "peach",
        }));
        today.checkins.slice(0, 1).forEach((c, i) => rows.push({
            key: `ci-${i}`, testid: `today-checkin-${i}`,
            title: "Daily check-in",
            detail: c.note ? `${c.label || c.code} — "${c.note}"` : (c.label || c.code),
            when: "Today", tone: "teal",
        }));
        today.wellness.slice(0, 1).forEach((w, i) => rows.push({
            key: `w-${i}`, testid: `today-wellness-${i}`,
            title: "Wellness check-in",
            detail: w.note ? `"${(w.note || "").slice(0, 90)}"` : "Recorded",
            when: "Today", tone: "sage",
        }));
        return rows.slice(0, 6);
    }, [today]);

    const hasIntake = !!(intake && (intake.updated_at || intake.created_at || intake.predx || intake.estimated_stage));
    const hasStory = !!(story && (story.loved_one?.name || story.caregiver?.name || story.updated_at));
    const hasWellness = !!(wellness && (wellness.last_check_in || (wellness.recent || []).length > 0));
    const isEmptyState = !loading && !hasIntake && !hasStory && !hasWellness && !lovedName;

    // Minimal first-visit home: greeting + check-in only, until they tap.
    const minimalHome = firstVisit && !firstTapped;
    useEffect(() => {
        setFirstVisitMode(minimalHome);
        return () => setFirstVisitMode(false);
    }, [minimalHome]);

    // "Today's Focus" chip row removed (Jul 2026): it duplicated the
    // Daily Check-In options one screen below. One touchpoint, less noise.

    return (
        <div
            className="relative max-w-[880px] w-[calc(100%-40px)] mx-auto ml-v2-stagger"
            data-testid="dashboard-today-view"
        >
            {/* Coastal light — a soft dawn wash behind everything. */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 md:-inset-x-24 -top-16 h-[540px] -z-10"
                style={{
                    background:
                        "radial-gradient(60% 55% at 18% 8%, rgba(230,240,242,0.9) 0%, rgba(230,240,242,0) 70%)," +
                        "radial-gradient(70% 60% at 55% 30%, rgba(163,201,199,0.18) 0%, rgba(163,201,199,0) 72%)",
                }}
            />

            <div className="space-y-4">
                <CaregiverOrgBadge />
                {drip.kind && !drip.dismissed && (
                    <DripWelcomeBanner
                        kind={drip.kind}
                        testid="dashboard-drip-banner"
                        message={`Lane's weekly check-in. Here's the whole picture for ${drip.lovedOneName || "your loved one"} — pick one thing that would take pressure off this week.`}
                        onDismiss={drip.dismiss}
                    />
                )}
            </div>

            {/* ---------------- Welcome — the emotional center ---------------- */}
            <section data-testid="dashboard-welcome" className="relative pt-10 md:pt-14 pb-2">
                <svg aria-hidden="true" className="absolute right-0 top-6 hidden md:block pointer-events-none opacity-80" width="300" height="150" viewBox="0 0 300 150" fill="none">
                    <path d="M-10 140 C 90 136, 110 52, 200 48 L 320 42" stroke="#9EAB98" strokeWidth="2.5" strokeDasharray="1 12" strokeLinecap="round" opacity="0.7" />
                    <circle cx="200" cy="48" r="5.5" fill="#12776F" opacity="0.8" />
                    <circle cx="200" cy="48" r="13" fill="#12776F" opacity="0.1" />
                    <path d="M252 74 q6 -15 0 -24 q-6 9 0 24z" fill="#A3C9C7" opacity="0.55" />
                </svg>
                <div className="relative max-w-2xl">
                    <h1
                        className="ml-v2-serif text-4xl sm:text-5xl font-medium tracking-tight text-[#12776F] leading-[1.12]"
                        data-testid="dashboard-greeting"
                    >
                        {timeGreeting()}{cgName ? `, ${cgName}` : ""}.
                    </h1>
                    <p className="mt-4 text-lg md:text-xl text-[#2A3330]/80 leading-relaxed max-w-xl">
                        {isEmptyState ? (
                            <span data-testid="dashboard-empty-state-copy">
                                This space is yours. Nothing has to be filled out today — look around at your pace,
                                and Lane is here whenever you&apos;re ready.
                            </span>
                        ) : (
                            <>Whatever today brings, you don&apos;t have to carry it alone.<br className="hidden sm:block" />
                                Let&apos;s simply focus on today.</>
                        )}
                    </p>
                    {!minimalHome && primaryArea && (
                        <p className="mt-3 text-sm text-[#4F7F95]" data-testid="dashboard-focus-line">
                            You&apos;re walking through{" "}
                            <span className="font-semibold" style={{ color: primaryArea.color }}>{primaryArea.title.toLowerCase()}</span>
                            {" "}right now. Lane is here whenever something feels different.
                        </p>
                    )}
                    {!minimalHome && (
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <Link to="/chat" data-testid="dashboard-talk-to-lane"
                            className="inline-flex items-center gap-2 rounded-2xl bg-[#12776F] text-[#FDFBF7] px-6 py-3 text-sm font-semibold hover:bg-[#0F615A] transition-colors duration-300 ml-v2-soft-shadow">
                            <MessageCircle size={15} strokeWidth={1.6} /> Talk to Lane
                        </Link>
                        {/* "Share today's update" pill removed (Jul 2026) — same
                            action lives in the care-team band below and the
                            Care Summary tab. One path, less noise. */}
                        <button type="button" onClick={() => setCareDetailsOpen(true)}
                            className="text-sm font-medium text-[#4F7F95] hover:text-[#12776F] transition-colors duration-300 inline-flex items-center gap-1.5 min-h-[44px]"
                            data-testid="dashboard-update-care-details">
                            <PencilLine size={13} strokeWidth={1.6} /> Update care details
                        </button>
                    </div>
                    )}
                </div>
            </section>

            {/* ---------------- Caregiver first: how are YOU? ---------------- */}
            {!loading && (
                <CaregiverCheckIn firstVisit={firstVisit} onResponded={() => setCaregiverDone(true)} />
            )}

            {/* ------- Loved-one check-in, directly beneath (returning users) ------- */}
            {!loading && !firstVisit && (
                <div id="daily-checkin" className="mt-6"><DailyCheckIn /></div>
            )}

            {/* ---------------- Post-intake acknowledgment ---------------- */}
            {planJustReady && !loading && intake && (
                <PlanReadyCard intake={intake} onDismiss={() => navigate("/dashboard", { replace: true })} />
            )}

            {/* ---------------- First visit: loved-one check-in, revealed
                 after the caregiver has answered about themselves ---------------- */}
            {firstVisit && caregiverDone && (
                <div className="mt-4 md:mt-6">
                    <FirstVisitCheckIn onTapped={() => setFirstTapped(true)} />
                </div>
            )}

            <CareDetailsDialog
                open={careDetailsOpen}
                onOpenChange={setCareDetailsOpen}
                sid={sid}
                profile={profile}
                onSaved={(patch) => setProfile(p => ({ ...(p || {}), ...patch }))}
            />

            {/* ------- Lane's briefing: done-for-you, before they arrived ------- */}
            {!minimalHome && <BriefingStack sid={sid} />}

            {/* ---------------- Today's priority ---------------- */}
            {!minimalHome && attention.length > 0 && (
                <section className="mt-12" data-testid="dashboard-today-changes">
                    <SectionEyebrow>Today&apos;s priority</SectionEyebrow>
                    <h2 className="ml-v2-serif text-2xl text-[#2A3330] mt-2">One thing at a time.</h2>
                    <ul className="mt-5 space-y-3" data-testid="today-priority-list">
                        {attention.map(a => (
                            <li key={a.key} data-testid={`major-${a.key}`}
                                className="rounded-2xl bg-white px-6 py-5 border border-[#E4E8EC] shadow-[0_1px_2px_rgba(16,42,67,0.04)]">
                                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                                    <p className="text-[15px] font-semibold text-[#2A3330]">{a.title}</p>
                                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#64748B]">{a.when}</span>
                                </div>
                                {a.detail && <p className="mt-1 text-sm text-[#2A3330]/70 leading-relaxed">{a.detail}</p>}
                                <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1">
                                    {a.to && a.cta && (
                                        <Link to={a.to} data-testid={`major-cta-${a.key}`}
                                            className="text-sm font-semibold text-[#12776F] hover:underline underline-offset-4 inline-flex items-center gap-1.5 min-h-[44px] transition-colors duration-300">
                                            {a.cta} <ArrowRight size={12} strokeWidth={1.8} />
                                        </Link>
                                    )}
                                    <button type="button" onClick={() => snooze(a.key, 7)} data-testid={`major-snooze-${a.key}`}
                                        className="text-xs text-[#4F7F95] hover:text-[#12776F] inline-flex items-center min-h-[44px] transition-colors duration-300">
                                        Snooze a week
                                    </button>
                                    <button type="button" onClick={() => snooze(a.key, 30)} data-testid={`major-dismiss-${a.key}`}
                                        className="text-xs text-[#4F7F95] hover:text-[#12776F] inline-flex items-center min-h-[44px] transition-colors duration-300">
                                        Dismiss
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* ---------- Compact rooms — open the rest when you're ready ---------- */}
            <div className={minimalHome ? "mt-24 md:mt-32" : "mt-12"}>
                {!minimalHome && (
                    <p className="px-1 pb-1 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4F7F95]/70" data-testid="dashboard-rooms-label">
                        When you&apos;re ready
                    </p>
                )}
                {!minimalHome && (
                    <CollapsibleSection
                        id="lane" testid="dashboard-lane-section" icon={MessageCircle} colorKey="askLane"
                        title="Lane is with you"
                        summary={`What Lane remembers${lovedName ? ` about ${lovedName}` : ""} — pick up where you left off.`}
                    >
                        <div data-testid="dashboard-lane-notes" className="rounded-[1.6rem] p-1 bg-white/55 ml-v2-soft-shadow max-w-2xl">
                            <LaneMemoryCard sid={sid} />
                        </div>
                    </CollapsibleSection>
                )}

                {/* ---------------- Your Journey ---------------- */}
                <CollapsibleSection
                    id="journey" testid="dashboard-road-now" icon={MapIcon} colorKey="roadmap"
                    title="Your Journey"
                    summary={primaryArea ? `You are here: ${primaryArea.title}` : "Place your marker whenever you're ready — no wrong answer."}
                >
                    <RoadNowCard primaryArea={primaryArea} alsoAreas={alsoAreas} primaryKey={primaryKey}
                        sid={sid} onMoved={(key) => setProfile(p => ({ ...(p || {}), primary_journey_area: key }))} />
                </CollapsibleSection>
            </div>

            {!minimalHome && (<>
            {/* ---------------- The people helping you ---------------- */}
            <CollapsibleSection
                id="care-team" testid="dashboard-care-team-band" icon={HeartHandshake} colorKey="guide"
                title="The people helping you"
                summary="One gentle update keeps family and professionals on the same page."
            >
                <div className="rounded-[1.6rem] bg-white/60 backdrop-blur px-6 py-6 md:px-8 ml-v2-soft-shadow flex flex-wrap items-center justify-between gap-5">
                    <div className="max-w-md">
                        <h2 className="ml-v2-serif text-xl text-[#2A3330]">Keep everyone on the same page.</h2>
                        <p className="mt-1.5 text-sm text-[#2A3330]/65 leading-relaxed">
                            One gentle update travels to the family and professionals walking with you — only what you choose to share.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <Link to="/care-summary" data-testid="care-team-share-update"
                            className="inline-flex items-center gap-2 rounded-2xl bg-[#4A5F82] text-[#FDFBF7] px-6 py-3 text-sm font-semibold hover:bg-[#3E5170] transition-colors duration-300">
                            Share today&apos;s update <ArrowRight size={13} strokeWidth={1.8} />
                        </Link>
                        <Link to="/care-team" data-testid="care-team-open"
                            className="text-sm font-medium text-[#4A5F82] hover:text-[#3E5170] inline-flex items-center min-h-[44px] transition-colors duration-300">
                            See your care team
                        </Link>
                    </div>
                </div>
            </CollapsibleSection>

            {/* ---------------- Recent progress ---------------- */}
            <CollapsibleSection
                id="recent" testid="dashboard-recent-progress" icon={History} colorKey="careSummary"
                title="Recent progress"
                summary={recent.length === 0 ? "Quiet so far today — quiet days count too." : `${recent.length} gentle note${recent.length === 1 ? "" : "s"} from today.`}
            >
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs font-medium">
                    <Link to="/behavior" className="text-[#64748B] hover:text-[#3E4B5F] transition-colors duration-300 inline-flex items-center min-h-[44px]" data-testid="recent-view-all">All history</Link>
                    <Link to="/behavior" className="text-[#64748B] hover:text-[#3E4B5F] transition-colors duration-300 inline-flex items-center min-h-[44px]" data-testid="recent-add-note">Add a note</Link>
                    <Link to={`/chat?prompt=${encodeURIComponent("I'd like to talk through the recent changes we've been noticing.")}`} className="text-[#64748B] hover:text-[#3E4B5F] transition-colors duration-300 inline-flex items-center min-h-[44px]" data-testid="recent-talk-lane">Talk it through with Lane</Link>
                </div>
                {recent.length === 0 ? (
                    <p className="mt-2 text-sm text-[#2A3330]/60 leading-relaxed max-w-lg" data-testid="today-empty">
                        Today has been quiet so far — and quiet days count too. You can add a note,
                        check in with Lane, or simply keep going.
                    </p>
                ) : (
                    <ul className="mt-2 space-y-1" data-testid="today-list">
                        {recent.map(r => {
                            const dot = r.tone === "peach" ? "#4F7F95" : r.tone === "sage" ? "#1D8A85" : "#12776F";
                            return (
                                <li key={r.key} data-testid={r.testid} className="py-3.5 flex items-start gap-4 border-b border-[#9EAB98]/15 last:border-0">
                                    <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: dot }} aria-hidden="true" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                                            <p className="text-sm font-semibold capitalize text-[#2A3330]">{r.title}</p>
                                            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#A3C9C7]">{r.when}</span>
                                        </div>
                                        {r.detail && <p className="mt-0.5 text-sm leading-relaxed text-[#2A3330]/65">{r.detail}</p>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </CollapsibleSection>

            {/* ---------------- A place for you, too ---------------- */}
            <CollapsibleSection
                id="for-you" testid="dashboard-support-strip" icon={Heart} colorKey="wellness"
                title="A place for you, too"
                summary="A breath, gentle wellness, and the support that helps."
            >
                <h2 className="ml-v2-serif text-xl text-[#2A3330]" data-testid="dashboard-support-heading">
                    You are carrying a lot.
                </h2>
                <p className="mt-1.5 text-sm text-[#2A3330]/60" data-testid="dashboard-support-subtext">
                    Take a moment to reset, or reach for the support that helps.
                </p>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/wellness" data-testid="dashboard-support-wellness"
                        className="group rounded-2xl bg-[#F3F8F7] border border-[#E4E8EC] px-7 py-6 transition-shadow duration-500 hover:shadow-[0_8px_28px_rgba(16,76,92,0.08)]">
                        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#1D8A85]">Take a breath</p>
                        <p className="mt-2 ml-v2-serif text-lg text-[#2A3330] leading-snug">
                            Gentle support for stress, burnout, rest, and a small moment for yourself.
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#12776F]">
                            Go to caregiver wellness <ArrowRight size={13} strokeWidth={1.8} className="transition-transform duration-500 group-hover:translate-x-1" />
                        </span>
                    </Link>
                    <div className="flex flex-col justify-center gap-2.5">
                        {[
                            { key: "lane", label: "Talk to Lane", to: "/chat" },
                            { key: "coordination", label: "Care coordination", to: "/care-team" },
                            { key: "community", label: "Community support", to: "/care-network" },
                        ].map(({ key, label, to }) => (
                            <Link key={key} to={to} data-testid={`dashboard-support-${key}`}
                                className="flex items-center rounded-2xl bg-white border border-[#E4E8EC] px-5 py-3 text-sm font-medium text-[#2A3330] hover:border-[#12776F]/30 hover:bg-[#F7FAFA] transition-colors duration-300">
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>
            </CollapsibleSection>

            <div className="mt-10 space-y-8">
                {/* DailyCheckInCard (morning brief) removed (Jul 2026): the
                    briefing at the top is THE done-for-you block; the brief
                    still reaches families in the daily digest email. */}
                {/* Trial notice + Clarity Call card removed (Jul 2026, founder
                    decision): the caregiver home is a safe space — no purchase
                    or call-scheduling asks on the caregiver side. */}

                <section data-testid="dashboard-quote" className="py-4">
                    <blockquote data-testid="dashboard-quote-text" className="ml-v2-serif text-xl md:text-2xl text-[#2A3330]/85 leading-relaxed italic max-w-2xl">
                        &ldquo;{quote.text}&rdquo;
                    </blockquote>
                    <p data-testid="dashboard-quote-attribution" className="mt-3 text-sm text-[#4F7F95] not-italic">
                        — {quote.attribution}, written by another dementia caregiver
                    </p>
                </section>

                <OrgCodeRow sid={sid} />
                <p className="text-sm text-[#4F7F95]/80 italic pb-6">Take what helps. Leave the rest.</p>
            </div>
            </>)}
        </div>
    );
}

/* ------------------------------------------------------------------ */

/** Your Journey — the winding road, never a completion bar. */
function RoadNowCard({ primaryArea, alsoAreas, primaryKey, sid, onMoved }) {
    const [suggestion, setSuggestion] = useState(null);
    useEffect(() => {
        if (!sid) return;
        api.get(`/roadmap/stage-suggestion/${sid}`)
            .then(r => setSuggestion(r.data))
            .catch(() => { /* quiet — nudge is optional */ });
    }, [sid]);
    const suggestedArea = suggestion?.suggest && suggestion.suggested_key !== primaryKey
        ? JOURNEY_AREAS.find(a => a.key === suggestion.suggested_key)
        : null;

    const acceptSuggestion = async () => {
        try {
            await api.put(`/profile/${sid}`, { primary_journey_area: suggestion.suggested_key });
            toast.success("Your marker moved to Daily support. Change it back any time.");
            onMoved?.(suggestion.suggested_key);
            setSuggestion(null);
        } catch {
            toast.error("Couldn't save. Please try again.");
        }
    };
    const dismissSuggestion = () => {
        api.post(`/roadmap/stage-suggestion/${sid}/dismiss`).catch(() => { /* silent */ });
        setSuggestion(null);
    };

    return (
        <div data-testid="road-now-body">
            <h2 className="ml-v2-serif text-xl text-[#2A3330]">Where you are on the road.</h2>

            {primaryArea ? (
                <>
                    <p className="mt-2 text-sm text-[#2A3330]/70" data-testid="road-now-primary-line">
                        <span className="text-[#4F7F95]">You are here:</span>{" "}
                        <span className="font-semibold" style={{ color: primaryArea.color }}>{primaryArea.title}</span>
                    </p>
                    {alsoAreas.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="road-now-also-chips">
                            <span className="text-xs text-[#4F7F95]">Also walking through:</span>
                            {alsoAreas.map(a => (
                                <span key={a.key} className="rounded-2xl border px-2.5 py-0.5 text-[11px] font-medium"
                                    style={{ color: a.color, borderColor: `${a.color}55`, backgroundColor: a.color_soft }}>
                                    {a.title}
                                </span>
                            ))}
                        </div>
                    )}
                    <p className="mt-2.5 text-sm text-[#2A3330]/65 leading-relaxed max-w-xl" data-testid="road-now-sentence">
                        {primaryArea.tagline}
                    </p>
                </>
            ) : (
                <p className="mt-3 text-sm text-[#2A3330]/65 leading-relaxed max-w-xl" data-testid="road-now-unset">
                    You haven&apos;t placed your marker yet. There&apos;s no wrong answer — pick the area that
                    feels closest, and change it any time.
                </p>
            )}

            {/* Condensed five-stage road. Comfort care + grief support only
                appear once the caregiver's own marker reaches them. */}
            <div className="mt-6 rounded-[1.8rem] bg-white/60 backdrop-blur px-6 py-6 ml-v2-soft-shadow" data-testid="road-now-overview">
                <div className="relative flex items-start justify-between gap-1">
                    <svg aria-hidden="true" className="absolute left-1 right-1 top-[4px] w-[calc(100%-8px)] h-4" preserveAspectRatio="none" viewBox="0 0 100 10">
                        <path d="M0 6 C 15 2, 30 9, 50 5 S 85 2, 100 6" stroke="#A3C9C7" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeDasharray="0.5 3" />
                    </svg>
                    {dashboardJourneyAreas([primaryKey, ...alsoAreas.map(a => a.key)]).map(a => {
                        const current = a.key === primaryKey;
                        return (
                            <div key={a.key} className="relative flex flex-col items-center flex-1 min-w-0" data-testid={`road-dot-${a.key}`}>
                                {current ? (
                                    <span className="relative flex w-5 h-5 items-center justify-center">
                                        <span className="absolute inline-flex h-full w-full rounded-full opacity-30 animate-ping" style={{ backgroundColor: a.color, animationDuration: "2.4s" }} />
                                        <span className="relative w-4 h-4 rounded-full border-[3px] border-white shadow" style={{ backgroundColor: a.color }} />
                                    </span>
                                ) : (
                                    <span className="w-3 h-3 mt-1 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: a.color, opacity: 0.65 }} />
                                )}
                                <span className={`mt-2 hidden md:block text-[9px] leading-tight text-center ${current ? "font-bold text-[#2A3330]" : "text-[#4F7F95]/70"}`}>
                                    {dashboardShortTitle(a)}
                                </span>
                            </div>
                        );
                    })}
                </div>
                {primaryArea && (
                    <p className="mt-2 md:hidden text-[11px] text-[#2A3330]/70 text-center">
                        <span className="font-semibold" style={{ color: primaryArea.color }}>● {primaryArea.title}</span> — you are here
                    </p>
                )}
            </div>

            {/* Gentle progression nudge — data-informed, never automatic. */}
            {suggestedArea && (
                <div className="mt-4 rounded-[1.4rem] bg-[#E6F0F2]/60 px-5 py-4 flex flex-wrap items-center gap-x-4 gap-y-2"
                    data-testid="stage-suggestion">
                    <p className="text-sm text-[#2A3330]/75 flex-1 min-w-[220px]">
                        {suggestion.reason} Want to move your marker to{" "}
                        <span className="font-semibold" style={{ color: suggestedArea.color }}>{suggestedArea.title}</span>?
                    </p>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={acceptSuggestion} data-testid="stage-suggestion-accept"
                            className="rounded-2xl bg-[#6D8266] text-[#FDFBF7] hover:bg-[#5C7055] font-semibold px-4 py-1.5 text-xs transition-colors duration-300">
                            Move my marker
                        </button>
                        <button type="button" onClick={dismissSuggestion} data-testid="stage-suggestion-dismiss"
                            className="rounded-2xl text-[#4F7F95] hover:text-[#12776F] font-medium px-2 py-1.5 text-xs transition-colors duration-300">
                            Not yet
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/roadmap" data-testid="road-now-open-roadmap"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#6D8266] text-[#FDFBF7] hover:bg-[#5C7055] font-semibold px-6 py-3 min-h-[44px] text-sm transition-colors duration-300 ml-v2-soft-shadow">
                    Open Your Journey
                </Link>
                <Link to="/roadmap" data-testid="road-now-update"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/70 backdrop-blur border border-[#6D8266]/35 text-[#5C7055] hover:bg-white font-semibold px-6 py-3 min-h-[44px] text-sm transition-colors duration-300">
                    Update where we are
                </Link>
            </div>
        </div>
    );
}

function OrgCodeRow({ sid }) {
    const [code, setCode] = useState(() => {
        try { return sessionStorage.getItem("mlcn_pending_org_code") || ""; } catch { return ""; }
    });
    const [busy, setBusy] = useState(false);
    const [joined, setJoined] = useState(null);
    const submit = async () => {
        if (!code.trim()) return;
        setBusy(true);
        try {
            const r = await api.post("/org/redeem", { code: code.trim().toUpperCase(), session_id: sid });
            setJoined(r.data?.org?.name);
            try { sessionStorage.removeItem("mlcn_pending_org_code"); } catch { /* ignore */ }
            try { localStorage.removeItem(`mlcn_org_consent_dismissed_${r.data?.org?.id}`); } catch { /* ignore */ }
            window.dispatchEvent(new Event("ml-org-invited"));
            toast.success(`You're connected to ${r.data?.org?.name}.`);
        } catch {
            toast.error("We couldn't find that invite code.");
        } finally {
            setBusy(false);
        }
    };
    if (joined) {
        return (
            <p className="text-xs text-[#4F7F95]" data-testid="org-redeemed">
                Connected to <span className="font-medium text-[#2A3330]">{joined}</span>.
            </p>
        );
    }
    return (
        <details className="text-sm text-[#4F7F95]" data-testid="org-code-disclosure" open={!!code}>
            <summary className="cursor-pointer inline-flex items-center gap-1.5">
                {code ? "We saved your invite code — finish connecting" : "Have an invite code from your care provider?"}
            </summary>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                    data-testid="org-code-input"
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    placeholder="e.g. ABC23XYZ"
                    className="px-3.5 py-2 rounded-2xl bg-white/80 border border-[#A3C9C7]/40 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-[#A3C9C7]"
                />
                <button
                    type="button"
                    onClick={submit}
                    disabled={busy}
                    data-testid="org-code-submit"
                    className="px-4 py-2 min-h-[44px] rounded-2xl bg-[#12776F] text-[#FDFBF7] text-sm disabled:opacity-60 transition-colors duration-300 hover:bg-[#0F615A]"
                >
                    {busy ? "…" : "Connect"}
                </button>
            </div>
        </details>
    );
}

